import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import PasswordConfirmationDialog from "components/__reusable/PasswordConfirmDialog";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import {
  deleteClass,
  getClassesOfAcademicYear,
} from "features/class/classAction";
import { getDeletionToken, deleteClassWithToken } from "services/classServices";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import { CLASS_PATH } from "router/pathName";
import ClassSelectionDialog from "components/__reusable/ClassSelectionDialog";
import DeleteConfirmationDialog from "components/__reusable/DeleteConfirmationDialog";
import type { Class } from "types/Class";

const DeleteClassButton = () => {
  const { t } = useTranslation();
  const { classes, loading, viewedYear } = useAppSelector(
    (state) => state.class
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showClassSelection, setShowClassSelection] = useState(false);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteButtonClick = () => {
    setShowPasswordPrompt(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    setTokenLoading(true);
    try {
      const token = await getDeletionToken(password, "delete_class");
      localStorage.setItem("deletion_token", token);
      setShowPasswordPrompt(false);
      setShowClassSelection(true);
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "パスワードが正しくありません"
        )
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const handleClassSelect = (classItem: Class) => {
    setSelectedClass(classItem);
    setShowClassSelection(false);
    setShowFinalConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedClass) return;

    const token = localStorage.getItem("deletion_token");
    if (!token) {
      dispatch(setErrorStatus("認証トークンが見つかりません"));
      setShowFinalConfirmation(false);
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteClassWithToken(selectedClass.id, token);
      localStorage.removeItem("deletion_token");
      setShowFinalConfirmation(false);
      await dispatch(getClassesOfAcademicYear({ year: viewedYear }));
      dispatch(
        setSuccessStatus({
          title: "削除完了",
          message: "削除しました",
        })
      );
      navigate(CLASS_PATH, { replace: true });
    } catch (error: any) {
      setShowFinalConfirmation(false);
      dispatch(
        setErrorStatus(error.response?.data?.message || "エラーが発生しました")
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPasswordPrompt(false);
    setShowClassSelection(false);
    setShowFinalConfirmation(false);
    setSelectedClass(null);
    localStorage.removeItem("deletion_token");
  };

  return (
    <>
      <div className="flex justify-end">
        <button
          className="font-bold text-gray-500 underline"
          onClick={handleDeleteButtonClick}
        >
          {t("_class.delete_class_button", {
            defaultValue: "クラスを削除する",
          })}
        </button>
      </div>

      {showPasswordPrompt && (
        <PasswordConfirmationDialog
          onCancel={handleCancel}
          onConfirm={handlePasswordConfirm}
          isLoading={tokenLoading}
        />
      )}

      {showClassSelection && (
        <ClassSelectionDialog
          classes={classes}
          onCancel={handleCancel}
          onClassSelect={handleClassSelect}
        />
      )}

      {showFinalConfirmation && selectedClass && (
        <DeleteConfirmationDialog
          classItem={selectedClass}
          onCancel={handleCancel}
          onConfirm={handleConfirmDelete}
          isLoading={deleteLoading}
        />
      )}
    </>
  );
};

export default DeleteClassButton;
